import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GraduationCap, Loader2, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { isAxiosError } from 'axios';
import { Button } from './index';
import { getAcademyAdviezen, maakAcademyAdvies, trekAcademyAdviesIn } from '../services/medewerkerApi';
import type { AcademyCategorie } from '../services/medewerkerApi';

/**
 * Belagom Expert-Aanbeveling: de casemanager/bedrijfsarts adviseert vanuit het dossier
 * een Academy-categorie (en optioneel een thema) voor het herstel van de medewerker.
 * In het medewerkersportaal verschijnt dan de filter-badge "Mijn Advies-Traject" en
 * krijgen passende cursussen de badge "Geadviseerd door uw casemanager".
 */
export const AcademyAdviesKaart: React.FC<{ dossierId: string }> = ({ dossierId }) => {
  const { t } = useTranslation('professional');
  const queryClient = useQueryClient();
  const [hoofdgroep, setHoofdgroep] = useState<AcademyCategorie | ''>('');
  const [subId, setSubId] = useState<string>('');
  const [toelichting, setToelichting] = useState('');

  const queryKey = ['academy-adviezen', dossierId];
  const { data, isLoading, isError } = useQuery({ queryKey, queryFn: () => getAcademyAdviezen(dossierId), enabled: !!dossierId });

  const subs = useMemo(() => (data?.subcategorieen ?? []).filter((s) => s.hoofdgroep === hoofdgroep), [data?.subcategorieen, hoofdgroep]);

  const opslaan = useMutation({
    mutationFn: () => maakAcademyAdvies(dossierId, { hoofdgroep: hoofdgroep as AcademyCategorie, subcategorie_id: subId ? Number(subId) : null, toelichting: toelichting || null }),
    onSuccess: () => {
      toast.success(t('academyAdvies.opgeslagen', 'Advies opgeslagen. De medewerker ziet het advies-traject in de Academy.'));
      setHoofdgroep('');
      setSubId('');
      setToelichting('');
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (e: unknown) => {
      const msg = isAxiosError(e) ? (e.response?.data as { message?: string } | undefined)?.message : undefined;
      toast.error(msg ?? t('academyAdvies.fout', 'Advies kon niet worden opgeslagen.'));
    },
  });
  const intrekken = useMutation({
    mutationFn: (id: string) => trekAcademyAdviesIn(dossierId, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    onError: () => toast.error(t('academyAdvies.fout', 'Advies kon niet worden opgeslagen.')),
  });

  if (isLoading) return <Loader2 className="h-5 w-5 animate-spin text-gray-400" aria-label={t('common.loading', 'Laden…')} />;
  if (isError || !data) return <p className="text-sm text-red-600">{t('academyAdvies.laadfout', 'Adviezen konden niet worden geladen.')}</p>;

  const actief = data.adviezen.filter((a) => a.actief);
  const hoofdgroepen = Object.entries(data.hoofdgroepen) as Array<[AcademyCategorie, { label: string; emoji: string; omschrijving: string }]>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        {t('academyAdvies.uitleg', 'Adviseer een Academy-categorie voor het herstel (bijv. Fysieke Fitheid bij rugklachten). De medewerker kan de catalogus dan met één klik filteren op uw advies.')}
      </p>

      {!data.gekoppeld && (
        <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {t('academyAdvies.nietGekoppeld', 'Er is nog geen medewerker-account aan dit dossier gekoppeld; het advies wordt zichtbaar zodra de medewerker zich aanmeldt.')}
        </p>
      )}

      {actief.length > 0 && (
        <ul className="space-y-2">
          {actief.map((a) => {
            const hg = data.hoofdgroepen[a.hoofdgroep];
            return (
              <li key={a.id} className="flex items-start justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <div className="min-w-0 text-sm">
                  <p className="font-semibold text-gray-900">
                    <GraduationCap className="mr-1 inline h-4 w-4 text-emerald-600" aria-hidden="true" />
                    {hg?.emoji} {hg?.label ?? a.hoofdgroep}
                    {a.subcategorie_naam && <span className="font-normal text-gray-600"> · {a.subcategorie_naam}</span>}
                  </p>
                  {a.toelichting && <p className="mt-0.5 text-gray-600">{a.toelichting}</p>}
                  <p className="mt-0.5 text-xs text-gray-500">{new Date(a.created_at).toLocaleDateString('nl-NL')}</p>
                </div>
                <button
                  type="button"
                  onClick={() => intrekken.mutate(a.id)}
                  disabled={intrekken.isPending}
                  className="shrink-0 rounded p-1 text-gray-400 hover:text-red-600 disabled:opacity-50"
                  aria-label={t('academyAdvies.intrekken', 'Advies intrekken')}
                  title={t('academyAdvies.intrekken', 'Advies intrekken')}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <form
        className="grid gap-3 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (hoofdgroep) opslaan.mutate();
        }}
      >
        <label className="text-sm">
          <span className="mb-1 block font-medium text-gray-700">{t('academyAdvies.hoofdgroep', 'Categorie')}</span>
          <select
            value={hoofdgroep}
            onChange={(e) => {
              setHoofdgroep(e.target.value as AcademyCategorie | '');
              setSubId('');
            }}
            required
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#0B1F3A] focus:outline-none"
          >
            <option value="">{t('academyAdvies.kiesHoofdgroep', 'Kies een categorie…')}</option>
            {hoofdgroepen.map(([key, info]) => (
              <option key={key} value={key}>
                {info.emoji} {info.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="mb-1 block font-medium text-gray-700">{t('academyAdvies.thema', 'Thema (optioneel)')}</span>
          <select value={subId} onChange={(e) => setSubId(e.target.value)} disabled={!hoofdgroep} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#0B1F3A] focus:outline-none disabled:bg-gray-50">
            <option value="">{t('academyAdvies.heleCategorie', 'Hele categorie')}</option>
            {subs.map((s) => (
              <option key={s.id} value={s.id}>
                {s.emoji} {s.naam}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm sm:col-span-2">
          <span className="mb-1 block font-medium text-gray-700">{t('academyAdvies.toelichting', 'Toelichting voor de medewerker (optioneel)')}</span>
          <input
            type="text"
            value={toelichting}
            maxLength={500}
            onChange={(e) => setToelichting(e.target.value)}
            placeholder={t('academyAdvies.toelichtingPh', 'bijv. Rug- en houdingstraining ter ondersteuning van de re-integratie')}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#0B1F3A] focus:outline-none"
          />
        </label>
        <div className="sm:col-span-2">
          <Button type="submit" size="sm" disabled={!hoofdgroep || opslaan.isPending} loading={opslaan.isPending}>
            {t('academyAdvies.opslaan', 'Advies vastleggen')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AcademyAdviesKaart;
